import {
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableRow,
  withStyles,
  WithStyles
} from '@material-ui/core'
import fastSort from 'fast-sort'
import { TFunction } from 'i18next'
import { path } from 'ramda'
import { Fragment, useState } from 'react'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import {
  createApartment,
  deselectApartment,
  getApartmentMedia,
  searchApartments,
  selectApartment,
  updateAllApartmentCodes,
  updateStatus
} from '../../../actions/apartments'
import {
  Navigate,
  RemoveApartment,
  SelectApartment,
  UpdateStatus,
  GetApartmentMedia,
  CreateApartment,
  UpdateAllApartmentCodes,
  UpdateApartmentCode
} from '../../../actions/types'
import { listUsers } from '../../../actions/users'
import {
  selectApartmentById,
  selectIsApartmentsSearchLoading,
  selectSelectedApartmentId
} from '../../../selectors/apartments'
import { selectUsers } from '../../../selectors/users'
import { Apartment } from '../../../types/apartments'
import {
  IndexedApplications,
  StateInterface,
  IndexedUsersState
} from '../../../types/states'
import { Column } from '../../../types/tables'
import { getApartmentOwnerEmail } from '../../../utils/apartments'
import { defaultSortAdminApartments } from '../../../utils/sorting'
import { ApartmentsShowingProps } from '../Apartments'
import AdminApartmentsToolbar from './AdminApartmentsToolbar'
import ApartmentRow from './ApartmentRow'
import EnhancedTableHead from './EnhancedTableHead'
import SearchBox from './SearchBox'

const columnData: Column[] = [
  {
    id: 'status',
    disablePadding: false,
    label: 'apartments.table.column.status',
    sortable: true
  },
  {
    id: 'address',
    disablePadding: false,
    label: 'apartments.table.column.address',
    sortable: true
  },
  {
    id: 'applications',
    disablePadding: false,
    label: 'apartments.table.column.applications'
  },
  {
    id: 'rentalPrice',
    disablePadding: false,
    label: 'apartments.table.column.rentalPrice',
    sortable: true
  },
  {
    id: 'client',
    disablePadding: false,
    label: 'apartments.table.column.client',
    sortable: true
  },
  {
    id: 'phoneNumber',
    disablePadding: false,
    label: 'apartments.table.column.phoneNumber'
  },
  { id: 'code', disablePadding: false, label: 'apartments.table.column.code' },
  {
    id: 'createdAt',
    disablePadding: false,
    label: 'apartments.table.column.createdAt',
    sortable: true
  },
  {
    id: 'updatedAt',
    disablePadding: false,
    label: 'apartments.table.column.updatedAt',
    sortable: true
  },
  {
    id: 'applicationsViewedAt',
    disablePadding: false,
    label: 'apartments.table.column.applicationsViewedAt',
    sortable: true
  },
  {
    id: 'marketingDays',
    disablePadding: false,
    label: 'apartments.table.column.marketingDays',
    sortable: true,
    width: 100
  },
  {
    id: 'availableFromDate',
    disablePadding: false,
    label: 'apartments.table.column.availableFromDate',
    sortable: true,
    width: 110
  }
]
const colSpan = columnData.length

const styles = () => ({
  root: {
    backgroundColor: 'inherit'
  },
  normalRow: {
    cursor: 'pointer'
  },
  searchSpinner: {
    width: '100%',
    height: '100%',
    display: 'flex',
    marginTop: '150px',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dialog: {
    backgroundColor: 'inherit'
  }
})

const isApartmentSelected = (
  selectedApartment: Apartment,
  apartmentId: string
) => apartmentId === path(['apartmentId'], selectedApartment)

const renderEmptyRows = (emptyRows: number) =>
  emptyRows > 0 && (
    <TableRow style={{ height: 49 * emptyRows }}>
      <TableCell colSpan={colSpan} />
    </TableRow>
  )

const countEmptyRows = (
  rowsPerPage: number,
  itemsCount: number,
  page: number
) => rowsPerPage - Math.min(rowsPerPage, itemsCount - page * rowsPerPage)

const sortApartmentsByOrder = (
  apartments: Apartment[],
  users: IndexedUsersState,
  order: 'asc' | 'desc',
  orderBy: string
) => {
  if (orderBy === 'address') {
    return order === 'asc'
      ? fastSort(apartments).by({ asc: (u) => u.information?.address })
      : fastSort(apartments).by({ desc: (u) => u.information?.address })
  }
  if (orderBy === 'client') {
    return order === 'asc'
      ? fastSort(apartments).by({
          asc: (u) => getApartmentOwnerEmail(u, users)
        })
      : fastSort(apartments).by({
          desc: (u) => getApartmentOwnerEmail(u, users)
        })
  }
  if (orderBy === 'createdAt') {
    return order === 'asc'
      ? fastSort(apartments).by({ asc: (u) => u.createdAt })
      : fastSort(apartments).by({ desc: (u) => u.createdAt })
  }
  if (orderBy === 'updatedAt') {
    return order === 'asc'
      ? fastSort(apartments).by({ asc: (u) => u.updatedAt })
      : fastSort(apartments).by({ desc: (u) => u.updatedAt })
  }
  if (orderBy === 'applicationsViewedAt') {
    return order === 'asc'
      ? fastSort(apartments).by({ asc: (u) => u.applicationsViewedAt })
      : fastSort(apartments).by({ desc: (u) => u.applicationsViewedAt })
  }
  if (orderBy === 'status') {
    return order === 'asc'
      ? fastSort(apartments).by({ asc: (u) => u.status })
      : fastSort(apartments).by({ desc: (u) => u.status })
  }
  if (orderBy === 'rentalPrice') {
    return order === 'asc'
      ? fastSort(apartments).by({ asc: (u) => u.confirmation?.price })
      : fastSort(apartments).by({ desc: (u) => u.confirmation?.price })
  }
  if (orderBy === 'marketingDays') {
    return order === 'asc'
      ? fastSort(apartments).by({ asc: (u) => u.statusUpdatedAt })
      : fastSort(apartments).by({ desc: (u) => u.statusUpdatedAt })
  }
  if (orderBy === 'availableFromDate') {
    return order === 'asc'
      ? fastSort(apartments).by({
          asc: (u) =>
            u.switchedData?.availableFromDate ||
            u.confirmation?.availableFromDate
        })
      : fastSort(apartments).by({
          desc: (u) =>
            u.switchedData?.availableFromDate ||
            u.confirmation?.availableFromDate
        })
  }
  return apartments
}

interface PropsFromState {
  selectedApartment?: Apartment
  isApartmentsSearchLoading: boolean
  users: IndexedUsersState
}

interface DispatchProps {
  selectApartment: SelectApartment
  deselectApartment: () => void
  createApartmentAction: CreateApartment
  updateAllApartmentCodesAction: UpdateAllApartmentCodes
  getApartmentMedia: GetApartmentMedia
  updateStatus: UpdateStatus
  listUsersAction: () => void
  searchApartments: (value: string) => void
}

interface OwnProps {
  showingsProps: ApartmentsShowingProps
  navigate: Navigate
  removeApartment: RemoveApartment
  updateApartmentCode: UpdateApartmentCode
  apartments: Apartment[]
  applications: IndexedApplications
  t: TFunction
}

type AdminApartmentsProps = OwnProps &
  DispatchProps &
  PropsFromState &
  WithStyles

const AdminApartments = (props: AdminApartmentsProps) => {
  const [rowsPerPage, changeRowsPerPage] = useState(1000)
  const [page, changePage] = useState(0)
  const [order, setOrder] = useState<'asc' | 'desc'>('asc')
  const [orderBy, setOrderBy] = useState<string | undefined>(undefined)

  const {
    classes,
    apartments,
    applications,
    createApartmentAction,
    updateAllApartmentCodesAction,
    selectApartment,
    deselectApartment,
    listUsersAction,
    searchApartments,
    t,
    showingsProps,
    selectedApartment,
    removeApartment,
    navigate,
    updateStatus,
    updateApartmentCode,
    users
  } = props
  const tableHeadProps = {
    columnData,
    order,
    orderBy,
    setOrder,
    setOrderBy,
    t
  }
  const apartmentsCount = apartments.length
  const emptyRows = countEmptyRows(rowsPerPage, apartmentsCount, page)
  const sortedApartments = orderBy
    ? sortApartmentsByOrder(apartments, users, order, orderBy)
    : defaultSortAdminApartments(apartments, applications, showingsProps)
  return (
    <Fragment>
      <SearchBox
        onChange={(value: string) => {
          searchApartments(value)
        }}
      />
      <AdminApartmentsToolbar
        t={t}
        createApartment={createApartmentAction}
        updateAllApartmentCodes={updateAllApartmentCodesAction}
        apartments={apartments}
      />
      {props.isApartmentsSearchLoading && (
        <div className={classes.searchSpinner}>
          <CircularProgress />
        </div>
      )}
      {!props.isApartmentsSearchLoading && (
        <Fragment>
          <TablePagination
            component='div'
            count={apartmentsCount}
            rowsPerPageOptions={[50, 100, 1000, 5000]}
            rowsPerPage={rowsPerPage}
            page={page}
            backIconButtonProps={{
              'aria-label': 'Previous Page'
            }}
            nextIconButtonProps={{
              'aria-label': 'Next Page'
            }}
            onChangePage={(event: any, page: number) => {
              changePage(page)
            }}
            onChangeRowsPerPage={(event: any) => {
              changeRowsPerPage(event.target.value)
            }}
          />
          <Table className={classes.table}>
            <EnhancedTableHead {...tableHeadProps} />
            <TableBody>
              {!sortedApartments && <LinearProgress />}
              {sortedApartments
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((apartment: Apartment) => {
                  const rowProps = {
                    classes,
                    apartment,
                    apartmentApplications: applications[apartment.apartmentId],
                    isSelected: selectedApartment
                      ? isApartmentSelected(
                          selectedApartment,
                          apartment.apartmentId
                        )
                      : false,
                    showingsProps,
                    t,
                    deselectApartment,
                    selectApartment,
                    removeApartment,
                    listUsersAction,
                    navigate,
                    updateStatus,
                    updateApartmentCode,
                    colSpan,
                    users
                  }
                  return (
                    <ApartmentRow key={apartment.apartmentId} {...rowProps} />
                  )
                })}
              {renderEmptyRows(emptyRows)}
            </TableBody>
          </Table>
          <TablePagination
            component='div'
            count={apartmentsCount}
            rowsPerPageOptions={[50, 100, 1000, 5000]}
            rowsPerPage={rowsPerPage}
            page={page}
            backIconButtonProps={{
              'aria-label': 'Previous Page'
            }}
            nextIconButtonProps={{
              'aria-label': 'Next Page'
            }}
            onChangePage={(event: any, page: number) => {
              changePage(page)
            }}
            onChangeRowsPerPage={(event: any) => {
              changeRowsPerPage(event.target.value)
            }}
          />
        </Fragment>
      )}
    </Fragment>
  )
}

const mapStateToProps = (state: StateInterface) => ({
  selectedApartment: selectApartmentById(selectSelectedApartmentId(state))(
    state
  ),
  isApartmentsSearchLoading: selectIsApartmentsSearchLoading(state),
  users: selectUsers(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createApartmentAction: () => dispatch(createApartment()),
  searchApartments: (value: string) => dispatch(searchApartments(value)),
  selectApartment: (apartmentId: string) =>
    dispatch(selectApartment(apartmentId)),
  deselectApartment: () => dispatch(deselectApartment()),
  getApartmentMedia: (apartmentId: string) =>
    dispatch(getApartmentMedia(apartmentId)),
  updateStatus: (status: string) => dispatch(updateStatus(status)),
  listUsersAction: () => dispatch(listUsers()),
  updateAllApartmentCodesAction: (apartments: Apartment[]) =>
    dispatch(updateAllApartmentCodes(apartments))
})

export default connect<PropsFromState, DispatchProps, OwnProps, StateInterface>(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(AdminApartments))
